// Painting the clip editor: the ruler, the warp-marker strip and the
// waveform, all drawn in clip beats. The waveform is drawn WARPED, the way
// Live draws it. Each pixel column is a span of beats; the marker map says
// which file seconds that span plays, and the column shows those seconds'
// min and max. A stretched segment spreads out and a squeezed one packs in,
// so every beat lines up with the grid. Pure canvas calls: nothing here reads
// React or the DOM.

import { beatAtFileSeconds, fileSecondsAt, type WarpMarker } from "@web-shared/lib/warp";
import { beatToX, gridFor, xToBeat, type ClipView } from "@web-shared/components/clip-editor/interactions";

export const RULER_H = 18;
export const MARKER_H = 16;
export const MARKER_COLOR = "#f2c230";

export interface EditorPaint {
  width: number;
  height: number;
  view: ClipView;
  /** Beats per bar, for the ruler's numbering. */
  beatsPerBar: number;
  markers: readonly WarpMarker[];
  /** Whether markers are shown (a warped clip). */
  warped: boolean;
  fallbackBpm: number;
  /** The object's length, in file seconds. */
  fileSec: number;
  /** Interleaved min/max over [envFromSec, envFromSec + envSec) of the file;
   * null before it has decoded. */
  env: Float32Array | null;
  envFromSec: number;
  envSec: number;
  /** Transients, in file seconds. */
  transients: readonly number[];
  startMarker: number;
  endMarker: number;
  loop: { start: number; end: number } | null;
  selected: number | null;
  hover: { kind: "marker"; index: number } | { kind: "transient"; sec: number } | null;
  /** The clip's colour, as `#rrggbb`. */
  color: string;
  /** Height of the marker strip; taller on a touch screen, so a thumb can
   * find a handle. Defaults to MARKER_H. */
  markerH?: number;
  /** Depth of the band along the waveform's top where hits can be grabbed;
   * shaded faintly so it reads as a place to reach for. */
  tickBand?: number;
}

/** The min/max of the envelope over file seconds [s0, s1), as [min, max]. */
function span(p: EditorPaint, s0: number, s1: number): [number, number] | null {
  const env = p.env;
  if (!env || s1 <= 0 || s0 >= p.fileSec) return null;
  const n = env.length >> 1;
  const per = p.envSec / n;
  const a = Math.floor((Math.max(s0, 0) - p.envFromSec) / per);
  const b = Math.ceil((Math.min(s1, p.fileSec) - p.envFromSec) / per);
  if (b <= 0 || a >= n) return null;
  let lo = 0;
  let hi = 0;
  for (let i = Math.max(0, a); i < Math.min(n, Math.max(a + 1, b)); i++) {
    lo = Math.min(lo, env[2 * i]!);
    hi = Math.max(hi, env[2 * i + 1]!);
  }
  return [lo, hi];
}

export function paintEditor(g: CanvasRenderingContext2D, p: EditorPaint): void {
  const { width, height, view } = p;
  const markerH = p.markerH ?? MARKER_H;
  const waveTop = RULER_H + markerH;
  const waveH = Math.max(1, height - waveTop);
  const mid = waveTop + waveH / 2;

  // Background, with the outside of the start/end markers dimmed (what the
  // clip does not play).
  g.fillStyle = "#15151a";
  g.fillRect(0, 0, width, height);
  const x0 = Math.max(0, beatToX(view, p.startMarker));
  const x1 = Math.min(width, beatToX(view, p.endMarker));
  if (x1 > x0) {
    g.fillStyle = "#1d1d24";
    g.fillRect(x0, waveTop, x1 - x0, waveH);
  }
  // Past the end of the audio but inside the clip: hatched, so a short
  // recording reads as "the audio stops here" rather than a waveform that
  // failed to draw.
  const audioEnd = p.warped ? beatAtFileSeconds(p.markers, p.fileSec, p.fallbackBpm) : (p.fileSec * p.fallbackBpm) / 60;
  const xa = Math.max(x0, beatToX(view, audioEnd));
  if (x1 - xa > 2) {
    g.save();
    g.beginPath();
    g.rect(xa, waveTop, x1 - xa, waveH);
    g.clip();
    g.fillStyle = "#15151a";
    g.fillRect(xa, waveTop, x1 - xa, waveH);
    g.strokeStyle = "rgba(255,255,255,0.05)";
    g.lineWidth = 1;
    g.beginPath();
    for (let x = xa - waveH; x < x1; x += 8) {
      g.moveTo(x, height);
      g.lineTo(x + waveH, waveTop);
    }
    g.stroke();
    g.restore();
  }

  // Grid: bars bright, beats fainter, subdivisions faintest.
  const grid = gridFor(view.pxPerBeat);
  const first = Math.floor(xToBeat(view, 0) / grid) * grid;
  const last = xToBeat(view, width);
  g.font = "10px ui-monospace, SFMono-Regular, Menlo, monospace";
  g.textBaseline = "middle";
  for (let b = first; b <= last; b += grid) {
    const x = Math.round(beatToX(view, b)) + 0.5;
    const bar = Math.abs(b / p.beatsPerBar - Math.round(b / p.beatsPerBar)) < 1e-6;
    const beat = Math.abs(b - Math.round(b)) < 1e-6;
    g.strokeStyle = bar ? "rgba(255,255,255,0.16)" : beat ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)";
    g.beginPath();
    g.moveTo(x, bar ? 0 : RULER_H - 5);
    g.lineTo(x, height);
    g.stroke();
    if (bar && b >= 0 && view.pxPerBeat * p.beatsPerBar >= 28) {
      g.fillStyle = "rgba(255,255,255,0.55)";
      g.fillText(String(Math.round(b / p.beatsPerBar) + 1), x + 3, RULER_H / 2);
    }
  }
  g.strokeStyle = "rgba(255,255,255,0.12)";
  g.beginPath();
  g.moveTo(0, RULER_H + 0.5);
  g.lineTo(width, RULER_H + 0.5);
  g.moveTo(0, waveTop + 0.5);
  g.lineTo(width, waveTop + 0.5);
  g.stroke();

  // Loop brace in the ruler; start/end flags on the waveform's edge.
  if (p.loop) {
    const lx0 = beatToX(view, p.loop.start);
    const lx1 = beatToX(view, p.loop.end);
    g.fillStyle = "rgba(160,160,170,0.35)";
    g.fillRect(lx0, 2, Math.max(1, lx1 - lx0), 5);
  }
  for (const [beat, label] of [
    [p.startMarker, "S"],
    [p.endMarker, "E"],
  ] as const) {
    const x = Math.round(beatToX(view, beat)) + 0.5;
    if (x < -10 || x > width + 10) continue;
    g.strokeStyle = "rgba(255,255,255,0.5)";
    g.beginPath();
    g.moveTo(x, waveTop);
    g.lineTo(x, height);
    g.stroke();
    g.fillStyle = "rgba(255,255,255,0.7)";
    g.fillText(label, label === "S" ? x + 3 : x - 10, waveTop + 8);
  }

  // The waveform, column by column through the map.
  if (p.env) {
    const half = waveH / 2 - 2;
    const cols = Math.ceil(width);
    const tops: number[] = [];
    const bots: number[] = [];
    let open = false;
    const flush = (upTo: number): void => {
      if (!open) return;
      const n = tops.length;
      g.beginPath();
      for (let i = 0; i < n; i++) g.lineTo(upTo - n + i + 0.5, tops[i]!);
      for (let i = n - 1; i >= 0; i--) g.lineTo(upTo - n + i + 0.5, bots[i]!);
      g.closePath();
      g.fill();
      tops.length = 0;
      bots.length = 0;
      open = false;
    };
    g.fillStyle = `${p.color}d9`;
    for (let c = 0; c < cols; c++) {
      const s0 = p.warped ? fileSecondsAt(p.markers, xToBeat(view, c), p.fallbackBpm) : (xToBeat(view, c) * 60) / p.fallbackBpm;
      const s1 = p.warped ? fileSecondsAt(p.markers, xToBeat(view, c + 1), p.fallbackBpm) : (xToBeat(view, c + 1) * 60) / p.fallbackBpm;
      const mm = span(p, s0, s1);
      if (!mm) {
        flush(c);
        continue;
      }
      open = true;
      tops.push(mid - Math.max(0.5, mm[1] * half));
      bots.push(mid - Math.min(-0.5, mm[0] * half));
    }
    flush(cols);
  }

  if (!p.warped) return;

  // Transients: ticks down the grab band along the waveform's top.
  const band = p.tickBand ?? 0;
  if (band > 0) {
    g.fillStyle = "rgba(255,255,255,0.03)";
    g.fillRect(0, waveTop, width, band);
  }
  g.strokeStyle = "rgba(255,255,255,0.4)";
  g.beginPath();
  for (const t of p.transients) {
    const x = Math.round(beatToX(view, beatAtFileSeconds(p.markers, t, p.fallbackBpm))) + 0.5;
    if (x < 0 || x > width) continue;
    g.moveTo(x, waveTop);
    g.lineTo(x, waveTop + Math.max(8, band * 0.6));
  }
  g.stroke();
  if (p.hover?.kind === "transient") {
    const x = Math.round(beatToX(view, beatAtFileSeconds(p.markers, p.hover.sec, p.fallbackBpm))) + 0.5;
    g.strokeStyle = "rgba(242,194,48,0.5)";
    g.setLineDash([3, 3]);
    g.beginPath();
    g.moveTo(x, RULER_H);
    g.lineTo(x, height);
    g.stroke();
    g.setLineDash([]);
    handle(g, x, "rgba(242,194,48,0.45)", markerH);
  }

  // Warp markers: a handle in the strip and a line through the waveform.
  p.markers.forEach((m, i) => {
    const x = Math.round(beatToX(view, m.beat)) + 0.5;
    if (x < -8 || x > width + 8) return;
    const active = p.selected === i || (p.hover?.kind === "marker" && p.hover.index === i);
    g.strokeStyle = active ? MARKER_COLOR : "rgba(242,194,48,0.55)";
    g.beginPath();
    g.moveTo(x, waveTop);
    g.lineTo(x, height);
    g.stroke();
    handle(g, x, p.selected === i ? "#ffe07a" : MARKER_COLOR, markerH);
  });
}

/** A marker's handle: a downward pentagon in the marker strip. */
function handle(g: CanvasRenderingContext2D, x: number, fill: string, markerH: number): void {
  const top = RULER_H + 2;
  const bottom = RULER_H + markerH - 1;
  const w = markerH > MARKER_H ? 8 : 5;
  g.fillStyle = fill;
  g.beginPath();
  g.moveTo(x - w, top);
  g.lineTo(x + w, top);
  g.lineTo(x + w, bottom - w);
  g.lineTo(x, bottom);
  g.lineTo(x - w, bottom - w);
  g.closePath();
  g.fill();
}
